import { BaseWebComponentDataProvider } from '../../../framework/BaseWebComponentDataProvider';
import {
  ConsoleRequest,
  DataProviderType,
  Company,
  ReadLicenseUsageDetailsPayload,
} from 'apps/webcomponents/src/utils/request';
import {
  AddExclusionResponse,
  ColumnKeys,
  ComputersAndVirtualMachinesViewData,
  ComputersAndVmItem,
  DeleteRetrieveQuarantineFilePayload,
  DeleteRetrieveQuarantineFileResponse,
  DeleteSelectedPayload,
  DeleteSelectedResponse,
  DownloadSelectedResponse,
  EmptyQuarantinePayload,
  ExclusionItemsMethods,
  QuarantineEndpointMethod,
  QuarantineFilesMethod,
  RestoreQuarantinePayload,
  RetrieveQuarantineFilePayload,
  RetrieveQuarantineFileResponse,
  SubmitToLabsFilePayload,
  ValidateQuarantineFilePayload,
  ValidateRetrievePayload,
} from '../types';
import { ExclusionListTypes, Module, ObjectType } from '../../../configurationProfilesApp/exclusionsPanel/types';
import { ReadLicenseUsageDetailsResponse } from '../../../utils/response';
import { Filter } from '../../../grid/gridFilters/types';

export class ComputersAndVirtualMachinesBaseDataProvider extends BaseWebComponentDataProvider {
  private endpoint = 'QuarantineEndpoints';
  private quarantineFilesEndpoint = 'QuarantineFiles';
  private dataProviderType = DataProviderType.ENDPOINTS;

  public async restoreQuarantine(payload: RestoreQuarantinePayload): Promise<any> {
    return await ConsoleRequest.createRequest<any>({
      dataProviderType: this.dataProviderType,
      payload,
      endpoint: this.endpoint,
      method: QuarantineEndpointMethod.CREATE_RESTORE_TASK,
    });
  }

  public async deleteSelected(payload: DeleteSelectedPayload): Promise<DeleteSelectedResponse> {
    return await ConsoleRequest.createRequest<DeleteSelectedResponse>({
      dataProviderType: this.dataProviderType,
      payload,
      endpoint: this.endpoint,
      method: QuarantineEndpointMethod.CREATE_REMOVE_TASK,
    });
  }

  public async emptyQuarantine(payload: EmptyQuarantinePayload): Promise<DeleteSelectedResponse> {
    return await ConsoleRequest.createRequest<DeleteSelectedResponse>({
      dataProviderType: this.dataProviderType,
      endpoint: this.endpoint,
      method: QuarantineEndpointMethod.CREATE_REMOVE_ALL_TASK,
      payload,
    });
  }

  public async validateRetrieveAction(payload: ValidateRetrievePayload): Promise<void> {
    return await ConsoleRequest.createRequest<void>({
      dataProviderType: this.dataProviderType,
      endpoint: this.endpoint,
      method: QuarantineEndpointMethod.VALIDATE_RETRIEVE_ACTION,
      payload,
    });
  }

  /**
   * Validate quarantine retrieved file
   *
   * @param payload
   */
  public async validateRetrievedFile(payload: ValidateQuarantineFilePayload): Promise<void> {
    return await ConsoleRequest.createRequest<void>({
      dataProviderType: this.dataProviderType,
      endpoint: this.quarantineFilesEndpoint,
      method: QuarantineFilesMethod.VALIDATE_RETRIEVED_FILE,
      payload,
    });
  }

  public async readLicenseUsageDetails(payload: ReadLicenseUsageDetailsPayload): Promise<ReadLicenseUsageDetailsResponse> {
    return Company.readLicenseUsageDetails(payload);
  }

  public async submitToLabs(payload: SubmitToLabsFilePayload): Promise<void> {
    return ConsoleRequest.createRequest<void>({
      dataProviderType: this.dataProviderType,
      endpoint: this.endpoint,
      method: QuarantineEndpointMethod.SUBMIT_TO_LABS,
      payload,
    });
  }

  public async addExclusion(payload: Array<ComputersAndVmItem>): Promise<AddExclusionResponse> {
    const records = Array<any>();
    payload.forEach(record => {
      records.push({
        companyId: this.dependencies.getCurrentUserCompany().id,
        type: ExclusionListTypes.ANTIMALWARE,
        description: '',
        specifics: {
          type: ObjectType.FILE,
          path: record.source.filePath,
          modules: [Module.ON_DEMAND, Module.ON_ACCESS],
        },
      });
    });

    let response;
    try {
      response = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'ExclusionItems',
        method: ExclusionItemsMethods.CREATE_RECORDS,
        payload: { records: records },
      });
    } catch (e) {
      console.error(e);
    }

    if (!response || !response.success) {
      return { success: false, data: [] };
    }

    return response;
  }

  public async downloadSelected(selectedIds: string): Promise<DownloadSelectedResponse> {
    const url = ConsoleRequest.buildDataProviderUrl(this.dataProviderType, 'downloadQuarantine')!;
    const form = new FormData();

    form.append('selectedItems', selectedIds);

    return fetch(url, { method: 'POST', body: form! }).then(async response => {
      const contentDispositionHeader = response.headers.get('content-disposition')!;
      const fileName = contentDispositionHeader.split('filename=')[1];

      return { fileName, response: await response.blob() };
    });
  }

  public async retrieveQuarantineFile(payload: RetrieveQuarantineFilePayload): Promise<RetrieveQuarantineFileResponse> {
    return await ConsoleRequest.createRequest<RetrieveQuarantineFileResponse>({
      dataProviderType: this.dataProviderType,
      endpoint: this.endpoint,
      method: QuarantineEndpointMethod.RETRIEVE_QUARANTINE_FILE,
      payload,
    });
  }

  /**
   * Create a request to delete retrieved quarantine archive
   * @param payload
   */
  public async deleteRetrieveQuarantinedFile(payload: DeleteRetrieveQuarantineFilePayload): Promise<DeleteRetrieveQuarantineFileResponse> {
    return await ConsoleRequest.createRequest<DeleteRetrieveQuarantineFileResponse>({
      dataProviderType: this.dataProviderType,
      endpoint: this.endpoint,
      method: QuarantineEndpointMethod.DELETE_RETRIEVED_QUARANTINE_FILE,
      payload
    });
  }

  public getDataForView(externalFilters: Filter<ColumnKeys>[]): ComputersAndVirtualMachinesViewData {
    return { externalFilters };
  }
}
